import {
  Baby,
  BookOpen,
  Building2,
  Bus,
  CalendarCheck,
  CheckCircle2,
  ClipboardCheck,
  ClipboardList,
  FileCheck2,
  GraduationCap,
  HeartPulse,
  IndianRupee,
  Layers3,
  Megaphone,
  Route,
  School,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";

const media = (base, width, height, alt, generator) => Object.freeze({
  alt,
  base: `/marketing/solutions/v1/${base}`,
  generator,
  height,
  widths: Object.freeze([640, 960, 1280, width].filter((value, index, values) => (
    value <= width && values.indexOf(value) === index
  ))),
  width,
});

const module = (icon, title, description, slug, availability = "Available now") => Object.freeze({
  availability,
  description,
  icon,
  slug,
  title,
});

const workflow = (icon, eyebrow, title, description, evidence) => Object.freeze({
  description,
  evidence,
  eyebrow,
  icon,
  title,
});

const faq = (question, answer) => Object.freeze({ answer, question });

const rollout = (title, description) => Object.freeze({ description, title });

/**
 * One route/content contract for every public solution page and the /solutions hub.
 * Marketing copy stays evidence-aware: K-12 capabilities are described directly, while
 * college/coaching entries keep their explicit fit-review and proposal boundaries.
 */
export const SOLUTION_REGISTRY = Object.freeze({
  school: Object.freeze({
    audience: "K-12 schools",
    badge: "K-12 schools",
    heroLabel: "One connected operating layer for the school day",
    headline: "Run the whole school day from one connected system.",
    subhead: "Bring admissions, attendance, academics, fee collection, people, transport, tasks, reports and family communication into one permission-aware workspace—built around the way Indian schools actually operate.",
    noun: "school",
    icon: School,
    media: media(
      "school-campus",
      1672,
      941,
      "A fictional contemporary Indian K-12 campus during morning arrival, with students, teachers, parents and school staff coordinating across the courtyard.",
      "GPT Image",
    ),
    seo: Object.freeze({
      title: "School ERP for K–12 Schools in India | Vidyapeeth360",
      description: "Run admissions, attendance, invoice-led fee collection, exams, published report cards and configured parent communication for an Indian K–12 school.",
    }),
    outcomes: Object.freeze([
      Object.freeze(["50+ modules", "across 9 connected workspaces"]),
      Object.freeze(["One student record", "used across authorised workflows"]),
      Object.freeze(["UDISE+ / APAAR", "fields and readiness controls included"]),
    ]),
    workflowHeading: "Follow one school day without rebuilding the same context.",
    workflowIntro: "Each hand-off keeps the underlying student, guardian and school record connected. Teams still see only the data their role permits.",
    workflows: Object.freeze([
      workflow(CalendarCheck, "Morning", "Attendance becomes an operational signal", "Teachers mark attendance in the configured class or period workflow. Authorised teams can review corrections and configured parent alerts without maintaining a second register.", "Recorded against the same student and timetable context"),
      workflow(IndianRupee, "Collections", "Invoices, receipts and dues stay reconcilable", "Finance teams work from invoice-led collections, exact receipt evidence and supervised recovery queues instead of loose payment notes.", "Exact paise-based finance records and reviewable receipts"),
      workflow(ClipboardCheck, "Academic cycle", "Marks move into reviewed, published results", "Exam schedules, marks entry, result review and report-card publication remain distinct states so a draft never looks published.", "Published output remains separate from editable marks"),
      workflow(Megaphone, "Family communication", "Updates use configured channels", "Notices and approved messages can use the channels the school has configured, while the source school record remains authoritative.", "Provider availability is explicit, never assumed"),
    ]),
    modules: Object.freeze([
      module(GraduationCap, "Student admission", "Capture enquiries, review applications and carry accepted student context into enrolment.", "student-admission"),
      module(CalendarCheck, "Student attendance", "Class- or period-aware attendance with correction and evidence workflows.", "student-attendance"),
      module(IndianRupee, "Fee collection", "Invoice-led collection, receipts, dues and provider-aware online payments.", "fee-collection"),
      module(ClipboardList, "Result publishing", "Separate marks entry, review and publication so families see only released results.", "result-publishing"),
      module(ShieldCheck, "Compliance AI", "Deterministic readiness checks with supported AI explanation when enabled.", "compliance-ai"),
      module(Sparkles, "Aira assistant", "Permission-scoped questions about supported school records in 11 languages.", "aira-assistant", "Aira tier · metered credits"),
    ]),
    evidenceId: "timetable-workspace",
    fit: Object.freeze([
      Object.freeze({ label: "Best fit", value: "Schools that want one governed record across administration, academics, finance and communication." }),
      Object.freeze({ label: "Review before rollout", value: "Payment, messaging, voice, transport and device providers are checked against the school’s configuration." }),
      Object.freeze({ label: "Data boundary", value: "Role permissions and source records stay authoritative; generated answers do not replace them." }),
    ]),
    rollout: Object.freeze([
      rollout("Map the school", "Confirm campuses, academic structure, roles, providers and the first workflows to release."),
      rollout("Validate the data", "Import supported student data through a dry run, correct mappings and review row-level issues before commit."),
      rollout("Configure safely", "Set permissions, approval boundaries, templates, fee structures and provider credentials with named owners."),
      rollout("Release in stages", "Train each role, verify outputs and expand only after the school accepts the previous stage."),
    ]),
    faqs: Object.freeze([
      faq("Can we start with only a few workflows?", "Yes. The rollout is scoped to the workflows and plan agreed for the school. Permissions, templates and provider readiness should be reviewed before each additional area is released."),
      faq("Does Vidyapeeth360 replace our payment or messaging provider?", "No. Online collection and outbound messaging depend on the provider configured for the school. The product keeps provider readiness and failures visible instead of implying every channel is always available."),
      faq("How is existing student data moved?", "The governed student import accepts CSV or XLSX, proposes a mapping and shows row-level validation in a dry run before commit. Broader migration scope should be confirmed during the fit review."),
      faq("What can Aira see?", "Aira uses supported, permission-checked tools. The asking person can request only records their role is already allowed to reach, and the underlying source record remains authoritative."),
    ]),
  }),

  preschool: Object.freeze({
    audience: "preschools and early-years teams",
    badge: "Preschools",
    heroLabel: "Calm daily operations for early years",
    headline: "A calmer preschool day—for families and every team behind it.",
    subhead: "Connect admissions, arrival, attendance, authorised care notes, fees, transport and parent updates around one child-and-guardian record.",
    noun: "preschool",
    icon: Baby,
    media: media(
      "preschool-arrival",
      1672,
      941,
      "A fictional Indian preschool morning handoff, with parents, children and early-years teachers in a bright supervised learning space.",
      "GPT Image",
    ),
    seo: Object.freeze({
      title: "Preschool & Playschool Management Software | Vidyapeeth360",
      description: "Admissions, daily attendance, fees, transport and parent communication for nursery, kindergarten and playschool operations—built for early years.",
    }),
    outcomes: Object.freeze([
      Object.freeze(["Parent-first", "arrival and communication context"]),
      Object.freeze(["Role-based", "access to child and health records"]),
      Object.freeze(["Invoice-led", "fee collection with receipts"]),
    ]),
    workflowHeading: "Make the sensitive moments clear, calm and accountable.",
    workflowIntro: "Early-years teams need less operational noise, not less control. The page focuses on the hand-offs families notice most.",
    workflows: Object.freeze([
      workflow(Users, "Arrival", "Start with the right child and guardian context", "Admissions and daily records keep guardian relationships attached to the child instead of relying on an informal contact list.", "Guardian and child context remain linked"),
      workflow(CalendarCheck, "Attendance", "Mark the day without a heavy workflow", "Teams can use the configured attendance flow and parent notification policy while corrections remain reviewable.", "Attendance and correction history remain distinct"),
      workflow(HeartPulse, "Care notes", "Keep health details behind the right permissions", "Allergy and medication notes are available only to authorised roles; they are not exposed as general classroom data.", "Sensitive records remain role restricted"),
      workflow(Megaphone, "Pickup and updates", "Use the channels the preschool actually configured", "Notices, attachments and approved updates follow configured providers and retain a clear source inside the school record.", "Channel readiness and delivery state stay explicit"),
    ]),
    modules: Object.freeze([
      module(Users, "Student admission", "Keep enquiry, guardian and enrolment context connected for the new-intake season.", "student-admission"),
      module(CalendarCheck, "Student attendance", "A simple daily record with configured notifications and correction evidence.", "student-attendance"),
      module(HeartPulse, "Health records", "Restrict allergies and medication notes to authorised staff.", "document-management"),
      module(IndianRupee, "Fee collection", "Term fees, pay links where configured and verified receipts.", "fee-collection"),
      module(Bus, "Transport management", "Routes, stops and rider assignments where transport is enabled.", "transport-management"),
      module(Megaphone, "Parent notification", "Approved notices and updates through configured channels.", "parent-notification"),
    ]),
    evidenceId: "fee-receipt",
    fit: Object.freeze([
      Object.freeze({ label: "Best fit", value: "Preschools that want a light daily workflow while keeping guardian, fee and sensitive child context governed." }),
      Object.freeze({ label: "Review before rollout", value: "Pickup, transport, health-note visibility and notification providers must match the preschool’s actual policy." }),
      Object.freeze({ label: "Data boundary", value: "Sensitive health and child records remain limited to authorised roles." }),
    ]),
    rollout: Object.freeze([
      rollout("Map children and guardians", "Confirm guardian relationships, classes, authorised staff and the minimum records needed for opening day."),
      rollout("Set daily boundaries", "Configure attendance, correction, health-note and communication permissions before staff training."),
      rollout("Connect providers", "Verify payment, messaging and transport dependencies that are part of the agreed scope."),
      rollout("Practice real scenarios", "Run arrival, absence, fee receipt and parent-update scenarios before releasing them to families."),
    ]),
    faqs: Object.freeze([
      faq("Can health notes be hidden from most staff?", "Yes. Health information is permission-aware and should be assigned only to authorised roles during configuration."),
      faq("Can parents receive automatic absence updates?", "The school can configure an attendance notification policy and an available delivery provider. A configured channel is required; the page does not assume every provider is active."),
      faq("Does the preschool need every module on day one?", "No. Start with the smallest useful daily flow, validate it with staff and families, then release the next agreed workflow."),
      faq("Can we collect fees online?", "Online collection is available when the school’s payment provider is configured and ready. Invoice, collection and receipt evidence remain separate records."),
    ]),
  }),

  college: Object.freeze({
    audience: "colleges and universities",
    badge: "Colleges & universities",
    heroLabel: "Programme, cohort and operations fit",
    headline: "A clearer operating map for every programme and cohort.",
    subhead: "Bring admissions, scheduling, assessments, finance, reporting and communication into a structured fit review—then launch the workflows that match your institution with scope and ownership defined.",
    noun: "college",
    icon: Building2,
    media: media(
      "college-campus",
      1536,
      857,
      "A fictional contemporary Indian higher-education campus with students and faculty moving through an academic quadrangle.",
      "Higgsfield",
    ),
    seo: Object.freeze({
      title: "College Management System Fit Review in India | Vidyapeeth360",
      description: "Review how Vidyapeeth360 admissions, fees, exams, scheduling and communication workflows map to an Indian college’s programmes, cohorts and compliance needs.",
    }),
    outcomes: Object.freeze([
      Object.freeze(["Programme mapping", "before commercial scope"]),
      Object.freeze(["Workflow evidence", "reviewed against real requirements"]),
      Object.freeze(["Explicit boundaries", "for providers and gated workspaces"]),
    ]),
    workflowHeading: "Shape the rollout around the way your institution actually operates.",
    workflowIntro: "Map the academic model first, then review assessment, finance and operational workflows with configuration, provider and availability boundaries kept explicit.",
    workflows: Object.freeze([
      workflow(Layers3, "Academic model", "Map programmes, cohorts and terms", "Review how programme, cohort, section and term structures relate to the existing class-and-section record model.", "A documented mapping is required before configuration"),
      workflow(ClipboardList, "Assessment", "Test exam and result rules against the model", "Walk through scheduling, marks, moderation and publication expectations, including the regulations that define a released result.", "Draft and published evidence must remain distinct"),
      workflow(IndianRupee, "Finance", "Define fee, GST and provider scope", "Model fee structures, online collection through the college’s provider, documents and dues tracking using concrete scenarios.", "Provider and commercial scope are confirmed in the proposal"),
      workflow(Route, "Operations", "Review department scheduling and gated areas", "Validate timetable, library, hostel and HR needs against product availability instead of assuming every K-12 workflow transfers unchanged.", "Availability gaps stay visible in the fit record"),
    ]),
    modules: Object.freeze([
      module(GraduationCap, "Student records", "Review how the student record maps to programmes, cohorts and departments.", "student-admission", "Fit review"),
      module(ClipboardList, "Exams and results", "Map schedules, marks and publication states to academic regulations.", "result-publishing", "Fit review"),
      module(IndianRupee, "Finance", "Scope fee structures, GST documents, providers and dues tracking.", "fee-collection", "Proposal scoped"),
      module(BookOpen, "Library management", "Review catalogue, issue/return and member needs.", "library-management", "Availability check"),
      module(CalendarCheck, "Timetable management", "Map department, faculty, room and substitution requirements.", "timetable-management", "Fit review"),
      module(Users, "HR and payroll", "Review staff, leave and statutory payroll workspace availability.", "payroll", "Availability check"),
    ]),
    evidenceId: null,
    fit: Object.freeze([
      Object.freeze({ label: "Best fit", value: "Institutions willing to validate programme, cohort, assessment and finance models before configuration." }),
      Object.freeze({ label: "Built for a deliberate fit", value: "The current record model is mapped against programme, cohort and regulatory needs before a college rollout is proposed." }),
      Object.freeze({ label: "Decision output", value: "A documented scope showing mapped workflows, provider dependencies, availability checks and gaps." }),
    ]),
    rollout: Object.freeze([
      rollout("Discovery workshop", "Bring programme, cohort, assessment, finance and identity requirements into one review."),
      rollout("Scenario mapping", "Walk real admissions, semester, exam and collection scenarios through the available product model."),
      rollout("Gap and provider review", "Record configuration work, unavailable items, gated workspaces and external-provider dependencies."),
      rollout("Proposal decision", "Proceed only when scope, ownership, evidence and release stages are explicit."),
    ]),
    faqs: Object.freeze([
      faq("Is Vidyapeeth360 a university SIS out of the box?", "No such claim is made. The product is built for Indian K-12 first. College use begins with a fit review of programmes, cohorts, regulations and operational scope."),
      faq("Which workflows can be reviewed?", "Admissions, student records, fees, exams, results, scheduling, library, hostel and HR requirements can be reviewed against current product availability and the proposed configuration."),
      faq("What happens when a requirement does not fit?", "It should be recorded as a gap, gated capability, provider dependency or proposal item. The rollout should not hide that boundary."),
      faq("Can we see the workflow before deciding?", "Yes. The fit review should use concrete institutional scenarios and reviewed product evidence where an exact current workflow is available."),
    ]),
  }),

  coaching: Object.freeze({
    audience: "coaching institutes and multi-centre groups",
    badge: "Coaching institutes",
    heroLabel: "One visible operating loop for every centre",
    headline: "Turn every enquiry, batch and instalment into one visible operating loop.",
    subhead: "Give centre teams a clearer path from first enquiry to enrolment, class attendance, tests, collections and approved parent updates—with branch ownership and rollout scope defined from the start.",
    noun: "coaching institute",
    icon: Sparkles,
    media: media(
      "coaching-centre",
      1536,
      857,
      "A fictional Indian coaching centre before an evening session, with staff, learners and an admissions conversation in a modern supervised space.",
      "Higgsfield",
    ),
    seo: Object.freeze({
      title: "Coaching Institute Management Software Fit Review | Vidyapeeth360",
      description: "Map coaching-centre batches, admissions, instalments, attendance, tests and parent updates onto a governed Vidyapeeth360 rollout.",
    }),
    outcomes: Object.freeze([
      Object.freeze(["Centre and batch map", "reviewed before configuration"]),
      Object.freeze(["Enquiry to enrolment", "kept in one supervised pipeline"]),
      Object.freeze(["Proposal-defined", "providers and rollout scope"]),
    ]),
    workflowHeading: "Connect the operating loop from first enquiry to the next class.",
    workflowIntro: "Coaching operations often live in separate lead sheets, attendance registers and dues lists. The fit review tests whether one governed model can carry those hand-offs.",
    workflows: Object.freeze([
      workflow(Users, "Enquiries", "Capture and assign the next follow-up", "Review how lead source, ownership, status and conversion evidence fit the institute’s admission process.", "Assignment and conversion remain reviewable"),
      workflow(Layers3, "Centres and batches", "Map the operating structure first", "Define centre, batch, subject, faculty and learner group relationships before configuring attendance or tests.", "No batch workflow is assumed before mapping"),
      workflow(CalendarCheck, "Class day", "Connect attendance to the right batch", "Use the mapped class-and-section attendance model and configure approved parent notifications where a provider is available.", "Attendance scope and delivery readiness stay explicit"),
      workflow(IndianRupee, "Instalments", "Track invoices, receipts and supervised recovery", "Review fee structures, instalments, pay links and recovery queues using the institute’s actual collection scenarios.", "Collection and provider evidence remain separate"),
    ]),
    modules: Object.freeze([
      module(Users, "Enquiry management", "Capture, assign and review conversion using the institute’s lead process.", "enquiry-management", "Fit review"),
      module(IndianRupee, "Fee collection", "Model instalments, receipts, dues and provider-backed payment links.", "fee-collection", "Fit review"),
      module(CalendarCheck, "Student attendance", "Map class-and-section attendance to batches before release.", "student-attendance", "Fit review"),
      module(ClipboardList, "Online exams", "Review test and result workflows against batch and subject needs.", "online-classes", "Availability check"),
      module(Building2, "Branch management", "Model centres, access boundaries and branch-level reporting requirements.", "branch-management", "Fit review"),
      module(Megaphone, "WhatsApp alerts", "Send approved-template updates through a configured provider.", "whatsapp-alerts", "Provider required"),
    ]),
    evidenceId: null,
    fit: Object.freeze([
      Object.freeze({ label: "Best fit", value: "Institutes ready to map centres, batches, fees and communication rules before rollout." }),
      Object.freeze({ label: "Review before rollout", value: "Batch attendance, online tests, messaging and payment dependencies must be tested against current availability." }),
      Object.freeze({ label: "Multi-centre boundary", value: "Branch access, ownership and reporting expectations belong in the fit record, not in assumptions." }),
    ]),
    rollout: Object.freeze([
      rollout("Map centres and batches", "Confirm branches, batches, subjects, faculty, fee structures and role boundaries."),
      rollout("Walk the lead journey", "Test enquiry assignment, follow-up, admission and learner creation with real scenarios."),
      rollout("Validate the class loop", "Run attendance, test, result, instalment and parent-update scenarios against available workflows."),
      rollout("Release centre by centre", "Train owners, verify evidence and expand to the next centre only after the previous stage is accepted."),
    ]),
    faqs: Object.freeze([
      faq("Does the product have a separate coaching-batch model?", "The current attendance foundation is the class-and-section model. The fit review maps the institute’s centres and batches onto it before any rollout claim is made."),
      faq("Can staff follow enquiries before admission?", "The admissions CRM can capture, assign and move enquiries through a reviewed pipeline. The exact stages and ownership rules should be mapped to the institute."),
      faq("Are WhatsApp and payment links always available?", "No. Both depend on configured providers and agreed scope. Readiness and delivery state should be verified before release."),
      faq("Can we start with one centre?", "Yes. A staged release can begin with one mapped centre, validate operations and evidence, then expand using the accepted configuration."),
    ]),
  }),
});

export const SOLUTION_KEYS = Object.freeze(Object.keys(SOLUTION_REGISTRY));

export function solutionFor(key) {
  return SOLUTION_REGISTRY[key] || null;
}

export function solutionFaqSchema(solution) {
  if (!solution?.faqs?.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: solution.faqs.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  };
}

export const SOLUTION_HUB_SEO = Object.freeze({
  title: "Education ERP Solutions by Institution Type | Vidyapeeth360",
  description: "Compare Vidyapeeth360 workflows for K-12 schools, preschools, colleges and coaching institutes, including fit boundaries and rollout expectations.",
});

export const SOLUTION_TRUST_POINTS = Object.freeze([
  Object.freeze({ icon: FileCheck2, label: "Evidence before rollout", detail: "Review real workflows, provider readiness and data boundaries before release." }),
  Object.freeze({ icon: ShieldCheck, label: "Permission-aware by design", detail: "Roles and source records stay authoritative across connected workflows." }),
  Object.freeze({ icon: CheckCircle2, label: "Honest fit boundaries", detail: "Mapped, gated, provider-dependent and unavailable items remain clearly separated." }),
]);
